import { Location } from '@angular/common';
import { Component, DestroyRef, inject, Inject, OnInit, Optional } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { RuntimeLoader } from '@rive-app/webgl2';
import { filter, tap } from 'rxjs';
import { environment } from 'shared/environments/environment';
import { AxonService } from 'shared/src/services/axon.service';
import { CustomizerSettingsService } from 'shared/src/services/customizer-settings.service';
import { GtmService } from 'shared/src/services/gtm.service';
import { NewsbreakService } from 'shared/src/services/newsbreak.service';
import { UserInfoService } from 'shared/src/services/user-info.service';
import { PROJECT_NAME, ProjectName, ProjectNames } from 'shared/src/tokens';
import { AnalyticsService } from '../../../shared/src/services/analytics.service';
import { slideInAnimation } from './animations/route-animations';
import { FunnelFlowService } from './services/funnel-flow.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [slideInAnimation],
  imports: [RouterOutlet, MatButtonModule],
})
export class AppComponent implements OnInit {
  isLogoShown: boolean = true;
  showBackButton: boolean = false;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private router: Router,
    private location: Location,
    private flowService: FunnelFlowService,
    private userInfo: UserInfoService,
    private analyticsService: AnalyticsService,
    private themeService: CustomizerSettingsService,
    private axonService: AxonService,
    private newsbreakService: NewsbreakService,
    private gtmService: GtmService,
    @Optional() @Inject(PROJECT_NAME) private projectName: ProjectName = ProjectNames.FUNNEL
  ) {}

  ngOnInit(): void {
    this.setTheme();
    this.axonService.init();
    this.newsbreakService.init();
    this.gtmService.init();

    this.handleUrlParams();
    this.handleQueryParams();
    this.startAnalytics();

    RuntimeLoader.setWasmUrl('lib/rive.wasm');
  }

  goBack(): void {
    if (this.router.url.includes('/checkout')) {
      this.router.navigate(['/paywall'], { queryParams: { fromCheckout: 'true' } });
    } else {
      this.location.back();
    }
  }

  // Function to determine the animation state based on the outlet and route
  prepareRoute(outlet: RouterOutlet) {
    if (!outlet || !outlet.activatedRouteData) {
      return undefined;
    }

    const url = this.router.url;

    // The intro keeps its own fade transition; every other screen animates by its index.
    if (url.startsWith('/quiz/') && !outlet.activatedRouteData['animation']) {
      const quizIndex = parseInt(url.split('/').pop() || '0', 10);
      return quizIndex; // Return the numeric index for increment/decrement animations
    }

    // For other routes, use the animation data from the route
    return outlet.activatedRouteData['animation'];
  }

  private async startAnalytics() {
    //Clarity.init(environment.clarityProjectId);

    const urlParams = new URLSearchParams(window.location.search);

    try {
      await this.analyticsService.init(environment.appVersion, true);

      this.analyticsService.track(
        'web_onboarding_started',
        this.flowService.getFunnelStartedAnalytics(),
        {
          user_agent: navigator.userAgent,
          fbp: this.getCookieValue('_fbp'), //facebook browser id
          web_funnel_version: environment.appVersion,
          project: this.projectName,
          keyword: urlParams.get('utm_keyword'),
          domain: location.hostname,
        }
      );
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  }

  private handleUrlParams() {
    this.checkLogoAndBackground();

    // Track initial page view
    this.axonService.pageView();
    // this.newsbreakService.pageView();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        tap(() => {
          this.checkLogoAndBackground();
          // Track page view on every navigation
          this.axonService.pageView();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private checkLogoAndBackground() {
    this.isLogoShown = !this.router.url.includes('paywall');
    const quizIndex = this.router.url.match(/^\/quiz\/(\d+)/)?.[1];

    this.showBackButton = quizIndex
      ? this.flowService.isBackAllowed(parseInt(quizIndex, 10))
      : !this.router.url.includes('paywall') &&
        !this.router.url.includes('login') &&
        !this.router.url.includes('post') &&
        !this.router.url.includes('payment-result') &&
        this.router.url !== '/quiz';
  }

  /**
   * Handle URL query parameters using plain JavaScript
   * This avoids Angular architecture dependencies because the router gets initialized late
   */
  private handleQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);

    this.userInfo.hydrateFromUrl(window.location.search);

    // Ad creatives that feature a specific book pass it along so the funnel
    // keeps the promise (e.g. ?book=bible).
    this.flowService.setPromotedBook(urlParams.get('book'));

    const keyword =
      urlParams.get('utm_keyword') || urlParams.get('utm_content') || urlParams.get('q');

    if (keyword) {
      this.flowService.setCompetitor(decodeURIComponent(keyword));
    }

    const title = urlParams.get('title');

    if (title) {
      this.flowService.setTitle(this.decodeTitleParam(title).trim());
    }
  }

  // URLSearchParams has already decoded the value once. Decoding again throws on a literal '%'
  // ("50% off"), which would abort the rest of ngOnInit, web_onboarding_started included.
  private decodeTitleParam(title: string): string {
    try {
      return decodeURIComponent(title);
    } catch {
      return title;
    }
  }

  private setTheme() {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;

    this.themeService.setTheme(prefersDark);

    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', e => {
      this.themeService.setTheme(e.matches);
    });
  }

  private getCookieValue(name: string): string {
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || '';
  }
}
